import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './CarouselModalContent.module.scss';
import ScrollArea from '../../components/ScrollArea';
import CarouselSliderButton from '../image/CarouselSliderButton';
import CarouselThumbnail from '../image/CarouselThumbnail';
import { useImageSrc } from '../image/ImageNode';
import type { UploadedCarouselItem } from '../image/types';
import useThumbnailBarAutoScroll from '../image/useThumbnailBarAutoScroll';

interface CarouselModalContentProps {
  images: UploadedCarouselItem[];
  selectedIndex: number;
}

const CarouselModalContent = ({
  images,
  selectedIndex: initialSelectedIndex,
}: CarouselModalContentProps) => {
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const selectedImage = images[selectedIndex];
  const src = useImageSrc(selectedImage ?? null, { fullscreen: true });
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  const { containerRef, scrollAreaRef, performScrolling, getTabRefCallback } =
    useThumbnailBarAutoScroll();
  const onSelectedIndexChangeEvent = useEffectEvent(performScrolling);
  useEffect(() => {
    onSelectedIndexChangeEvent(selectedIndex);
  }, [selectedIndex]);

  useEffect(() => {
    const previouslyFocusedElement = document.activeElement as HTMLElement;
    carouselContainerRef.current?.focus();
    return () => {
      previouslyFocusedElement?.focus?.({ preventScroll: true });
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      return;
    }

    const direction = e.key === 'ArrowRight' ? 1 : -1;
    setSelectedIndex(
      (selectedIndex + direction + images.length) % images.length,
    );
  };

  return (
    <div
      className={styles.container}
      tabIndex={-1}
      ref={carouselContainerRef}
      onKeyDown={onKeyDown}
    >
      <div className={styles.slider}>
        {src ? (
          <img
            draggable={false}
            className={styles.image}
            src={src}
            alt={selectedImage?.caption}
          />
        ) : null}
        {images.length > 1 && (
          <>
            <div className={styles.buttonPrevious}>
              <CarouselSliderButton
                selectedIndex={selectedIndex}
                onSelectedIndexChange={setSelectedIndex}
                imageCount={images.length}
                direction="previous"
              />
            </div>
            <div className={styles.buttonNext}>
              <CarouselSliderButton
                selectedIndex={selectedIndex}
                onSelectedIndexChange={setSelectedIndex}
                imageCount={images.length}
                direction="next"
              />
            </div>
          </>
        )}
        <div className={styles.caption}>{selectedImage?.caption}</div>
      </div>
      {images.length > 1 && (
        <div className={styles.thumbnailBar}>
          <ScrollArea
            direction="horizontal"
            className={styles.thumbnailBarScrollingContainer}
            ref={scrollAreaRef}
          >
            <div
              role="tablist"
              className={styles.thumbnailBarContent}
              ref={containerRef}
            >
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={classNames(styles.thumbnail, {
                    [styles.isSelected]: index === selectedIndex,
                  })}
                  ref={getTabRefCallback(index)}
                >
                  <CarouselThumbnail
                    index={index}
                    image={image}
                    selected={index === selectedIndex}
                    onSelectedIndexChange={setSelectedIndex}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default CarouselModalContent;
